import Image from 'next/image'
import { FunctionComponent } from 'react'
import { useMount } from 'react-use'
import styled from 'styled-components'
import arrowIconUrl from '@/assets/images/pptx/card-icon/arrow.png'
import eyeIconUrl from '@/assets/images/pptx/card-icon/eye.png'
import likeIconUrl from '@/assets/images/pptx/card-icon/like.png'
import messageIconUrl from '@/assets/images/pptx/card-icon/message.png'
import playIconUrl from '@/assets/images/pptx/card-icon/play.png'
import riseIconUrl from '@/assets/images/pptx/card-icon/rise.png'
import { OurIntlShape } from '@/i18n/typing'
import {
  designLayoutSize,
  platformBrandMap,
  PptxObjectType,
} from '@/utils/export-pptx/constants'
import { KdSlide } from '@/utils/export-pptx/types/slide'

interface SlideProps {
  slide: KdSlide
  onSlideReady: (slide: KdSlide) => void
  formatMessage: OurIntlShape['formatMessage']
}

const SlideComponent: FunctionComponent<SlideProps> = ({
  slide,
  onSlideReady,
  formatMessage,
}) => {
  useMount(() => {
    onSlideReady(slide)
  })

  return (
    <Wrapper>
      <BaseInfoWrapper>
        <ImageWrapper>
          <PlatformIcon>
            <Image
              priority
              unoptimized
              alt={slide.kolInfo.platform}
              data-link={slide.kolInfo.link}
              data-pptx-object={PptxObjectType.Image}
              data-z-index={100}
              height={72}
              src={platformBrandMap[slide.kolInfo.platform]}
              width={72}
            />
          </PlatformIcon>
          <Image
            priority
            unoptimized
            alt='kol thumbnail'
            data-link={slide.kolInfo.link}
            data-pptx-object={PptxObjectType.Image}
            height={265}
            src={slide.kolInfo.thumbnail}
            width={460}
          />
        </ImageWrapper>
        {slide.kolInfo.isHighPotential && (
          <HighPotentialTag>
            <Image
              alt='rise'
              data-pptx-object={PptxObjectType.Image}
              height={16}
              src={riseIconUrl}
              width={16}
            />{' '}
            <div data-pptx-object={PptxObjectType.Text}>
              {formatMessage({ id: 'kol:high_potential' })}
            </div>
          </HighPotentialTag>
        )}
        <KolName data-ellipsis data-pptx-object={PptxObjectType.Text}>
          {slide.kolInfo.name}
        </KolName>
        {slide.kolInfo.alias && (
          <Para data-pptx-object={PptxObjectType.Text}>
            {formatMessage({
              id: 'general:field_name_alias',
            })}
            {formatMessage({ id: 'general:colon' })}
            {slide.kolInfo.alias}
          </Para>
        )}
        {slide.kolInfo.gender && (
          <Para data-pptx-object={PptxObjectType.Text}>
            {formatMessage({ id: 'general:field_name_gender' })}
            {formatMessage({ id: 'general:colon' })}
            {slide.kolInfo.gender}
          </Para>
        )}
        {slide.kolInfo.property && (
          <Para data-pptx-object={PptxObjectType.Text}>
            {formatMessage({ id: 'general:field_name_property' })}
            {formatMessage({ id: 'general:colon' })}
            {slide.kolInfo.property}
          </Para>
        )}
        {slide.kolInfo.postQuotationRange && (
          <Para data-pptx-object={PptxObjectType.Text}>
            {formatMessage(
              { id: 'general:field_name_graphic_estimated_price' },
              { currencyCode: slide.currencyCode },
            )}
            {formatMessage({ id: 'general:colon' })}
            {slide.kolInfo.postQuotationRange}
          </Para>
        )}
        {slide.kolInfo.videoQuotationRange && (
          <Para data-pptx-object={PptxObjectType.Text}>
            {formatMessage(
              { id: 'general:field_name_video_estimated_price' },
              { currencyCode: slide.currencyCode },
            )}
            {formatMessage({ id: 'general:colon' })}
            {slide.kolInfo.videoQuotationRange}
          </Para>
        )}
        {slide.kolInfo.recentlyMentionedBrands && (
          <Para data-pptx-object={PptxObjectType.Text}>
            {formatMessage({
              id: 'general:field_name_recently_mentioned_brands',
            })}
            {formatMessage({ id: 'general:colon' })}
            {slide.kolInfo.recentlyMentionedBrands}
          </Para>
        )}
        <BasicInfoFooter>
          <Image
            data-original-size
            alt='logo'
            data-pptx-object={PptxObjectType.Image}
            height={37}
            src={slide.logoSrc}
            width={100}
          />
          <UpdateAtDesc data-pptx-object={PptxObjectType.Text}>
            {formatMessage(
              { id: 'pptx:description_data_source' },
              {
                date: slide.updateAt,
                period:
                  slide.period === 'three_months'
                    ? formatMessage({ id: 'general:period_3m' })
                    : formatMessage({ id: 'general:period_6m' }),
              },
            )}
          </UpdateAtDesc>
        </BasicInfoFooter>
      </BaseInfoWrapper>
      <KpiInfoWrapper>
        <Section>
          <SectionTitleBlock>
            <span data-pptx-object={PptxObjectType.Text}>
              {formatMessage({ id: 'general:section_title_basic_info' })}
            </span>
            <span data-pptx-object={PptxObjectType.Shape} />
          </SectionTitleBlock>
          <KpiList>
            {slide.kpi.map((kpi, i) => (
              <Kpi key={i}>
                <KpiTitle data-pptx-object={PptxObjectType.Text}>
                  {kpi.title}
                </KpiTitle>
                <KpiValue>
                  <KpiValuePrimary
                    color={kpi?.primaryColor}
                    data-pptx-object={PptxObjectType.Text}
                  >
                    {kpi.primaryText}
                  </KpiValuePrimary>
                  {(kpi?.secondaryTextData ?? []).map(
                    (secondaryTextData, i) => (
                      <KpiValueSecondary
                        color={secondaryTextData.color}
                        data-pptx-object={PptxObjectType.Text}
                        key={`KpiValueSecondary-${i}`}
                      >
                        {secondaryTextData.title}
                      </KpiValueSecondary>
                    ),
                  )}
                </KpiValue>
              </Kpi>
            ))}
          </KpiList>
        </Section>
        {slide.topPostTypes && (
          <Section>
            <SectionTitleBlock>
              <span data-pptx-object={PptxObjectType.Text}>
                {formatMessage({
                  id: 'general:section_title_top_5_posting_types',
                })}
              </span>
              <span data-pptx-object={PptxObjectType.Shape} />
            </SectionTitleBlock>
            <PostTypeList>
              {slide.topPostTypes.map((topPostType, i) => (
                <PostType key={i}>
                  <span data-pptx-object={PptxObjectType.Text}>
                    {topPostType.title}
                  </span>
                  <span data-pptx-object={PptxObjectType.Text}>
                    {topPostType.value}
                  </span>
                </PostType>
              ))}
            </PostTypeList>
          </Section>
        )}
        <Section>
          <SectionTitleBlock>
            <span data-pptx-object={PptxObjectType.Text}>
              {formatMessage({
                id: 'general:section_title_interactions_top_3_posts',
              })}
            </span>
            <span data-pptx-object={PptxObjectType.Shape} />
          </SectionTitleBlock>
          <PostList>
            {(slide.posts ?? []).slice(0, 3).map((post, i) => (
              <Post key={i}>
                <PostImage>
                  <Image
                    priority
                    unoptimized
                    alt='post thumbnail'
                    data-link={post.link}
                    data-pptx-object={PptxObjectType.Image}
                    height={134}
                    src={post.thumbnail}
                    width={215}
                  />
                </PostImage>
                <PostTitle data-ellipsis data-pptx-object={PptxObjectType.Text}>
                  {post.title.replaceAll('\n', ' ')}
                </PostTitle>
                <IconList>
                  {post.likeCount && (
                    <IconWrapper>
                      <Image
                        priority
                        unoptimized
                        alt='like'
                        data-pptx-object={PptxObjectType.Image}
                        height={16}
                        src={likeIconUrl}
                        width={16}
                      />
                      <span data-pptx-object={PptxObjectType.Text}>
                        {post.likeCount}
                      </span>
                    </IconWrapper>
                  )}
                  {post.commentCount && (
                    <IconWrapper>
                      <Image
                        priority
                        unoptimized
                        alt='message'
                        data-pptx-object={PptxObjectType.Image}
                        height={16}
                        src={messageIconUrl}
                        width={16}
                      />
                      <span data-pptx-object={PptxObjectType.Text}>
                        {post.commentCount}
                      </span>
                    </IconWrapper>
                  )}
                  {post.viewCount && (
                    <IconWrapper>
                      <Image
                        priority
                        unoptimized
                        alt='eye'
                        data-pptx-object={PptxObjectType.Image}
                        height={16}
                        src={eyeIconUrl}
                        width={16}
                      />
                      <span data-pptx-object={PptxObjectType.Text}>
                        {post.viewCount}
                      </span>
                    </IconWrapper>
                  )}
                  {post.playCount && (
                    <IconWrapper>
                      <Image
                        priority
                        unoptimized
                        alt='play'
                        data-pptx-object={PptxObjectType.Image}
                        height={16}
                        src={playIconUrl}
                        width={16}
                      />
                      <span data-pptx-object={PptxObjectType.Text}>
                        {post.playCount}
                      </span>
                    </IconWrapper>
                  )}
                  {post.shareCount && (
                    <IconWrapper>
                      <Image
                        priority
                        unoptimized
                        alt='arrow'
                        data-pptx-object={PptxObjectType.Image}
                        height={16}
                        src={arrowIconUrl}
                        width={16}
                      />
                      <span data-pptx-object={PptxObjectType.Text}>
                        {post.shareCount}
                      </span>
                    </IconWrapper>
                  )}
                </IconList>
              </Post>
            ))}
          </PostList>
        </Section>
      </KpiInfoWrapper>
    </Wrapper>
  )
}

const ImageWrapper = styled.div`
  position: relative;

  > img {
    border-radius: 10px;
    object-fit: cover;
  }
`

const PlatformIcon = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  transform: translate(-50%, -50%);
`

const HighPotentialTag = styled.div`
  border-radius: 2px;
  line-height: 20px;
  display: flex;
  align-items: center;
  color: ${({ theme }): string => theme.colors.brand.primary};

  img {
    margin-right: 2px;
  }
`

const KolName = styled.div`
  font-size: 32px;
  font-weight: 700;
  line-height: normal;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`

const Para = styled.div`
  display: flex;
  align-items: center;
  font-size: 20px;
  line-height: 1.3;
`

const BasicInfoFooter = styled.div`
  position: absolute;
  top: 727px;

  display: flex;
  gap: 10px;
  align-items: center;
`

const UpdateAtDesc = styled.div`
  top: 736px;
  padding: 0 4px;
  width: 352px;
`

const BaseInfoWrapper = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 591px;
  padding: 68px 66px 0 65px;
  background: ${({ theme }): string => theme.colors.background.white};

  ${ImageWrapper} {
    margin-bottom: 24px;
  }
  ${HighPotentialTag} {
    margin-bottom: 6px;
  }
  ${KolName} {
    margin-bottom: 10px;
  }
  ${Para} + ${Para} {
    margin-top: 6px;
  }
`

const KpiTitle = styled.div`
  font-size: 20px;
  line-height: 1.1;
`

const KpiValue = styled.div`
  font-weight: 700;
  line-height: 1.1;
  font-size: 26px;
`

const KpiValuePrimary = styled.span<{ color?: string }>`
  color: ${({ color, theme }): string => color ?? theme.colors.text.primary};
`

const KpiValueSecondary = styled.span<{ color?: string }>`
  color: ${({ color, theme }): string => color ?? theme.colors.brand.primary};
  font-size: 18px;
  margin-left: 4px;
`

const Kpi = styled.div`
  ${KpiTitle} {
    margin-bottom: 12px;
  }
`

const KpiList = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-bottom: -22px;

  ${Kpi} {
    flex: 1 1 22%;
    margin-bottom: 22px;
  }
`

const PostType = styled.div`
  font-size: 20px;
  line-height: 1.6;
  display: inline-block;

  span {
    display: inline-block;
  }

  span:last-child {
    font-weight: 700;
    margin-left: 4px;
    color: ${({ theme }): string => theme.colors.brand.primary};
  }
`

const PostTypeList = styled.div`
  ${PostType} {
    margin-right: 12px;
  }
`

const PostImage = styled.div`
  background: ${({ theme }): string => theme.colors.background.white};

  img {
    border-radius: 5px;
    object-fit: cover;
  }
`
const PostTitle = styled.div`
  font-size: 16px;
  line-height: 1.6;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`
const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
  line-height: 1;
  margin-bottom: 4px;
  color: ${({ theme }): string => theme.colors.text.secondary};

  img {
    margin-right: 3px;
  }

  span {
    padding: 2px;
  }
`
const IconList = styled.div`
  display: flex;
  flex-wrap: wrap;

  ${IconWrapper} {
    margin-right: 10px;
  }
`

const Post = styled.div`
  font-size: 24px;
  line-height: 1.6;

  ${PostImage} {
    margin-bottom: 16px;
  }

  ${PostTitle} {
    margin-bottom: 16px;
  }
`

const PostList = styled.div`
  display: flex;

  ${Post} {
    width: 215px;
    flex: 0 0 auto;
    margin-right: 48px;
  }
`

const SectionTitleBlock = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;

  span:first-child {
    flex: 0 0 auto;
    position: relative;
    font-size: 15px;
    line-height: 1.6;
    padding-right: 10px;
    color: ${({ theme }): string => theme.colors.text.secondary};
  }

  span:last-child {
    flex: 1 1 auto;
    height: 1px;
  }
`

const Section = styled.div`
  margin-top: 32px;

  &:first-child {
    margin-top: 0;
  }
`

const KpiInfoWrapper = styled.div`
  position: absolute;
  top: 61px;
  left: 632px;
  width: 766px;
`

const Wrapper = styled.div`
  font-family: Arial;
  width: ${designLayoutSize.w}px;
  height: ${designLayoutSize.h}px;
  position: relative;
  border: solid 1px ${({ theme }): string => theme.colors.text.primary};
  color: ${({ theme }): string => theme.colors.text.primary};

  &,
  * {
    box-sizing: border-box;
  }
`

export default SlideComponent
